/**
 * <ds-selectable-gifts>: Auswahl-UI für wählbare Geschenke im Cart-Drawer.
 *
 * Liegt bewusst als Asset statt als Inline-Script im Snippet: Das Snippet wird im
 * Drawer nur bei gefülltem Warenkorb gerendert. Inline-Scripts aus per Ajax
 * nachgeladenem Section-HTML führt der Browser nicht aus. Bei leerem Warenkorb
 * blieb das Element deshalb undefiniert, bis die Seite neu geladen wurde.
 * Geladen wird es in sections/ds-cart-drawer.liquid, unabhängig vom Warenkorb.
 */
if (!customElements.get('ds-selectable-gifts')) {
    class DSSelectableGifts extends HTMLElement {
        constructor() {
            super();
            this.grid = this.querySelector('[data-gift-grid]');
            this.slotsLeftEl = this.querySelector('[data-slots-left]');
            this.template = this.querySelector('#ds-gift-item-template');
            this.variantTemplate = this.querySelector('#ds-gift-variant-template');
            this.errorEl = this.querySelector('[data-gift-error]');
            this.errorText = {
                // Übersetzung kommt als Attribut aus dem Snippet (Assets werten kein Liquid aus).
                add: this.dataset.errorAdd || '',
            };
            this.storageKey = 'active_campaign';
            this.isAdding = false;

            this.onRequestStart = this.onRequestStart.bind(this);
            this.onRequestEnd = this.onRequestEnd.bind(this);
        }

        connectedCallback() {
            document.addEventListener('liquid-ajax-cart:request-start', this.onRequestStart);
            document.addEventListener('liquid-ajax-cart:request-end', this.onRequestEnd);

            document.addEventListener('liquid-ajax-cart:init', () => {
                this.cart = window.liquidAjaxCart.cart;
                this.render();
            });

            // Falls die gemeinsamen Regeln (ds-cart-ajax.js) erst nach dem Cart-Init
            // verfügbar werden, einmal nachrendern.
            document.addEventListener('ds-gift-rules:ready', () => {
                if (!this.cart && window.liquidAjaxCart) this.cart = window.liquidAjaxCart.cart;
                if (this.cart) this.render();
            }, { once: true });

            // Das Script lädt per defer, also oft erst nach "liquid-ajax-cart:init" und
            // "ds-gift-rules:ready". Dann sofort rendern, statt auf Events zu warten,
            // die schon vorbei sind.
            if (window.liquidAjaxCart && window.liquidAjaxCart.init && window.DSGiftRules) {
                this.cart = window.liquidAjaxCart.cart;
                this.render();
            }
        }

        disconnectedCallback() {
            document.removeEventListener('liquid-ajax-cart:request-start', this.onRequestStart);
            document.removeEventListener('liquid-ajax-cart:request-end', this.onRequestEnd);
        }

        onRequestStart() {
            this.classList.add('is-loading');
            this.querySelectorAll('button').forEach((btn) => (btn.disabled = true));
        }

        onRequestEnd(event) {
            this.classList.remove('is-loading');
            if (event.detail && event.detail.cart) {
                this.cart = event.detail.cart;
                this.render(); // baut das Grid neu auf (frische, korrekt aktivierte Buttons)
            } else {
                // Kein Re-Render → wenigstens die von onRequestStart deaktivierten
                // Buttons wieder freigeben.
                this.querySelectorAll('button').forEach((btn) => (btn.disabled = false));
            }
        }

        getCampaign() {
            try {
                const stored = localStorage.getItem(this.storageKey);
                return stored ? JSON.parse(stored) : null;
            } catch (e) {
                console.error('Error reading campaign from storage', e);
                return null;
            }
        }

        getDismissedIds() {
            const raw = this.cart?.attributes?._dismissed_gifts;
            if (!raw) return [];
            return String(raw)
                .split(',')
                .map((s) => parseInt(s.trim(), 10))
                .filter(Boolean);
        }

        showError(message) {
            if (!this.errorEl) return;
            this.errorEl.textContent = message;
            this.errorEl.hidden = false;
        }

        clearError() {
            if (!this.errorEl) return;
            this.errorEl.textContent = '';
            this.errorEl.hidden = true;
        }

        render() {
            const campaign = this.getCampaign();
            if (!campaign) {
                this.hidden = true;
                return;
            }

            // Berechtigung ausschließlich über die gemeinsamen Regeln (window.DSGiftRules,
            // definiert in ds-cart-ajax.js). Dieselbe Funktion entscheidet in der Mutation,
            // was im Warenkorb bleibt – die UI darf nichts anbieten, was dort sofort entfernt würde.
            const rules = window.DSGiftRules;
            if (!rules) {
                this.hidden = true;
                return;
            }

            const eligibleSteps = campaign.steps.filter(
                (step) => step.type === 'selectable_gifts' && rules.stepRequirementsMet(step, this.cart)
            );

            if (eligibleSteps.length === 0) {
                this.hidden = true;
                return;
            }

            this.grid.innerHTML = '';
            this.clearError();
            let totalSlotsLeft = 0;
            let hasRenderedGifts = false;

            eligibleSteps.forEach((step) => {
                const limit = step.gift_selection_limit || 1;
                const stepId = String(step.id);

                // Bereits hinzugefügte Geschenke dieses Steps zählen — variantenunabhängig
                // über das _custom_gift_step-Property (korrekt auch bei variantenreichen
                // Produkten, wo eine beliebige Größe hinzugefügt wurde).
                const currentCount = this.cart.items
                    .filter((item) => item.properties && item.properties._custom_gift_step === stepId)
                    .reduce((sum, item) => sum + item.quantity, 0);

                const slotsLeft = Math.max(0, limit - currentCount);
                totalSlotsLeft += slotsLeft;

                if (slotsLeft > 0) {
                    step.gift_products.forEach((product) => {
                        this.renderGift(product, stepId);
                        hasRenderedGifts = true;
                    });
                }
            });

            if (hasRenderedGifts) {
                this.hidden = false;
                this.slotsLeftEl.textContent = totalSlotsLeft;
            } else {
                this.hidden = true;
            }
        }

        renderGift(product, stepId) {
            const clone = this.template.content.cloneNode(true);
            const card = clone.querySelector('.ds-gift-item');
            const img = card.querySelector('.ds-gift-item__image');
            const addBtn = card.querySelector('[data-gift-add]');
            const variantsEl = card.querySelector('[data-gift-variants]');
            const statusEl = card.querySelector('[data-gift-status]');

            img.src = product.featured_image;
            img.alt = product.title;
            card.querySelector('.ds-gift-item__title').textContent = product.title;
            card.dataset.stepId = stepId;

            // Variantenliste (Fallback: einzelne Variante über variant_id)
            // Fallback nur, wenn Liquid keine Variantenliste geliefert hat. Ohne variant_id
            // (ausverkauft / Whitelist ohne Treffer) darf der Fallback nicht "verfügbar" sein.
            const variants = Array.isArray(product.variants) && product.variants.length
                ? product.variants
                : [{ id: product.variant_id, title: '', available: Boolean(product.variant_id) }];
            const availableVariants = variants.filter((v) => v.available);

            // Kein verfügbarer Bestand → Karte deaktiviert anzeigen (Reward bleibt sichtbar)
            if (availableVariants.length === 0) {
                card.classList.add('is-unavailable');
                statusEl.hidden = false;
                this.grid.appendChild(clone);
                return;
            }

            // Chips zeigen, wenn es mehrere Varianten gibt ODER genau eine benannte Variante
            // per gift_variants freigegeben wurde (z.B. nur Größe "M") – der Kunde soll sehen,
            // welche Größe er bekommt. Nur bei variant_scope "whitelist", damit Stores ohne das
            // neue Feld exakt wie bisher rendern.
            const hasNamedVariant = product.variant_scope === 'whitelist'
                && variants.length === 1
                && variants[0].title
                && variants[0].title !== 'Default Title';

            if (variants.length > 1 || hasNamedVariant) {
                // Varianten-Chips anbieten (bei genau einer verfügbaren wird sie vorausgewählt).
                variantsEl.hidden = false;
                variants.forEach((variant) => {
                    const chip = this.variantTemplate.content.firstElementChild.cloneNode(true);
                    chip.textContent = variant.title;
                    chip.dataset.variantId = variant.id;
                    if (!variant.available) {
                        chip.disabled = true;
                    } else {
                        chip.addEventListener('click', () => this.selectVariant(card, chip));
                    }
                    variantsEl.appendChild(chip);
                });

                // Genau eine verfügbare Variante → vorauswählen und Button freigeben.
                if (availableVariants.length === 1) {
                    const chip = variantsEl.querySelector(
                        `.ds-gift-item__variant[data-variant-id="${availableVariants[0].id}"]`
                    );
                    if (chip) this.selectVariant(card, chip);
                } else {
                    addBtn.disabled = true;
                }
            } else {
                // Einzel-Varianten-Produkt: direkt hinzufügbar.
                card.dataset.variantId = String(variants[0].id);
                addBtn.disabled = false;
            }

            addBtn.addEventListener('click', () => this.onAddClick(card));
            this.grid.appendChild(clone);
        }

        selectVariant(card, chip) {
            card.querySelectorAll('.ds-gift-item__variant').forEach((c) => {
                c.classList.remove('selected');
                c.setAttribute('aria-pressed', 'false');
            });
            chip.classList.add('selected');
            chip.setAttribute('aria-pressed', 'true');
            card.dataset.variantId = chip.dataset.variantId;
            card.querySelector('[data-gift-add]').disabled = false;
        }

        onAddClick(card) {
            const variantId = card.dataset.variantId;
            const stepId = card.dataset.stepId;
            if (!variantId) return;
            this.addGift(variantId, stepId, card);
        }

        // Fügt genau ein Geschenk (gewählte Variante) hinzu und bereinigt ggf. den
        // Dismiss-State. Bei Fehler (z.B. Variante zwischen Render und Klick vergriffen)
        // wird die betroffene Option gesperrt und ein sichtbarer Hinweis gezeigt.
        async addGift(variantId, stepId, card) {
            if (this.isAdding) return;
            this.isAdding = true;
            this.classList.add('is-loading');
            this.clearError();

            const items = [{
                id: variantId,
                quantity: 1,
                properties: { '_custom_gift_step': String(stepId) },
            }];

            const idInt = parseInt(variantId, 10);
            const dismissed = this.getDismissedIds();
            const nextDismissed = dismissed.filter((id) => id !== idInt);
            const dismissedChanged = nextDismissed.length !== dismissed.length;

            try {
                // Direkter /cart/add.js-Call, um die Response explizit prüfen zu können
                // (liquidAjaxCart.add() ist fire-and-forget und verschluckt Fehler).
                const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items }),
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    console.error('Gift add rejected by Shopify:', res.status, err);
                    throw new Error(err.description || err.message || `HTTP ${res.status}`);
                }
                // State neu ziehen (löst Mutations + Re-Render aus); Dismiss ggf. mitschicken.
                await window.liquidAjaxCart.update(
                    dismissedChanged
                        ? { attributes: { _dismissed_gifts: nextDismissed.join(',') } }
                        : {}
                );
            } catch (error) {
                console.error('Error adding gift to cart:', error);
                this.showError(this.errorText.add);
                this.markVariantUnavailable(card, variantId);
            } finally {
                this.isAdding = false;
                this.classList.remove('is-loading');
            }
        }

        // Nach fehlgeschlagenem Add: betroffene Variante sperren. Bei Mehr-Varianten-
        // Produkten nur den jeweiligen Chip, sonst die ganze Karte.
        markVariantUnavailable(card, variantId) {
            if (!card) return;
            const chip = card.querySelector(
                `.ds-gift-item__variant[data-variant-id="${variantId}"]`
            );
            const addBtn = card.querySelector('[data-gift-add]');
            if (chip) {
                chip.disabled = true;
                chip.classList.remove('selected');
                card.dataset.variantId = '';
                if (addBtn) addBtn.disabled = true;
            } else {
                card.classList.add('is-unavailable');
                const statusEl = card.querySelector('[data-gift-status]');
                if (statusEl) statusEl.hidden = false;
            }
        }
    }

    customElements.define('ds-selectable-gifts', DSSelectableGifts);
}
